import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import FormControlLabel, {
  FormControlLabelProps
} from '@mui/material/FormControlLabel';
import { BaseReactComponent } from '../../../types';

export interface HiCheckboxProps extends BaseReactComponent {
  /**
   * The checkbox label.
   */
  label?: FormControlLabelProps['label'];
  /**
   * If `true`, the component is checked.
   */
  checked?: CheckboxProps['checked'];
  /**
   * If `true`, the component appears indeterminate. This does not set the native input element to indeterminate due to inconsistent behavior across browsers. However, we set a `data-indeterminate` attribute on the `input`.
   */
  indeterminate?: CheckboxProps['indeterminate'];
  /**
   * If `true`, the component is disabled.
   */
  disabled?: CheckboxProps['disabled'];
  /**
   * Callback fired when the state is changed.
   */
  onChange?: CheckboxProps['onChange'];
  /**
   * HTML tab index
   */
  tabIndex?: number;
  /**
   * Override Mui Checkbox props
   * (https://mui.com/material-ui/api/checkbox/).
   */
  CheckboxProps?: CheckboxProps;
}

/**
 * Checkboxes allow the user to select one or more items from a set.
 * A checkbox control has three states: unselected, selected, and indeterminate.
 *
 * - Lay out your lists **vertically**.
 * - Your checkbox should **always be visible** (not only when hovering another element).
 * - When an option isn’t available, make your checkbox **disabled**.
 * - Don’t use checkbox as action button.
 * - Try to be concise for the label.
 *
 * It extends Mui's `Checkbox` component.
 *
 * [Learn when to use](https://uxplanet.org/checkbox-vs-toggle-switch-7fc6e83f10b8)
 */
const HiCheckbox = ({
  label,
  checked,
  indeterminate = false,
  disabled,
  onChange,
  CheckboxProps,
  ...other
}: HiCheckboxProps) => {
  const CheckboxComponent = (
    <Checkbox
      {...other}
      {...CheckboxProps}
      checked={checked}
      indeterminate={indeterminate}
      disabled={disabled}
      onChange={onChange}
      size="small"
      sx={{
        p: 2 // padding 8px
      }}
    />
  );

  // If label, render with FormControlLabel
  if (label) {
    return <FormControlLabel label={label} control={CheckboxComponent} />;
  }

  // If no label, render only Checkbox
  return CheckboxComponent;
};

export default HiCheckbox;
